import React, { useCallback, useMemo, useState } from 'react';
import { Card, Typography, Row, Drawer, Spin, Empty } from 'antd';
import { Link } from 'react-router-dom';
import { getInspectorKatipMissingContractCount, listInspectorKatipMissingContracts } from '../services/inspectorDashboardService';
import { GoogleDocIcon } from 'hugeicons-react';
import dayjs, { Dayjs } from 'dayjs';

const { Title } = Typography;

const normalizeString = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length ? trimmed : undefined;
  }
  return undefined;
};

const toUpperCaseTr = (value?: string): string | undefined => {
  if (!value) return undefined;
  return value.toLocaleUpperCase('tr-TR');
};

const pickAddressValue = (source: any, keys: string[]): string | undefined => {
  if (!source || typeof source !== 'object') {
    return undefined;
  }
  for (const key of keys) {
    const normalized = normalizeString((source as any)[key]);
    if (normalized) {
      return normalized;
    }
  }
  return undefined;
};

const resolveAddressText = (appointment: any): string | undefined => {
  const addresses = [appointment?.proposal?.address, appointment?.company?.address, appointment?.address];

  for (const address of addresses) {
    if (!address) continue;
    if (typeof address === 'string') {
      const normalized = normalizeString(address);
      if (normalized) {
        return normalized;
      }
    }
    const line1 = pickAddressValue(address, [
      'addressLine1',
      'addressLine',
      'fullAddress',
      'address',
      'street',
      'streetAddress',
      'address1',
    ]);
    const line2 = pickAddressValue(address, ['addressLine2', 'address2']);
    const title = pickAddressValue(address, ['title', 'name']);
    const combined = [line1, line2].filter(Boolean).join(' ');
    if (combined.length) {
      return combined;
    }
    if (title) {
      return title;
    }
  }

  return undefined;
};

const resolveCityDistrictText = (appointment: any): string | undefined => {
  const cityFromApi = toUpperCaseTr(normalizeString(appointment?.city));
  const districtFromApi = toUpperCaseTr(normalizeString(appointment?.district ?? appointment?.county));
  if (cityFromApi || districtFromApi) {
    const parts = [districtFromApi, cityFromApi].filter(Boolean);
    if (parts.length) {
      return parts.join(' / ');
    }
  }

  const addresses = [appointment?.proposal?.address, appointment?.company?.address, appointment?.address];

  for (const address of addresses) {
    if (!address || typeof address !== 'object') continue;
    const district = toUpperCaseTr(pickAddressValue(address, [
      'district',
      'districtName',
      'district_text',
      'districtText',
      'county',
      'countyName',
      'ilce',
      'town',
      'state',
    ]));
    const city = toUpperCaseTr(pickAddressValue(address, ['city', 'cityName', 'province', 'provinceName', 'il']));
    const parts = [district, city].filter(Boolean);
    if (parts.length) {
      return parts.join(' / ');
    }
  }

  return undefined;
};

const InspectorKatipSummaryCard: React.FC = () => {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [contracts, setContracts] = useState<any[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);

  const fetchCount = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getInspectorKatipMissingContractCount();
      const value = typeof response?.count === 'number' ? response.count : 0;
      setCount(value);
    } catch {
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchContracts = useCallback(async () => {
    try {
      setContractsLoading(true);
      const response = await listInspectorKatipMissingContracts();
      const filtered = Array.isArray(response)
        ? response.filter(
          (appointment) =>
            appointment?.contractId == null && appointment?.contractUrl == null,
        )
        : [];
      setContracts(filtered);
    } catch {
      setContracts([]);
    } finally {
      setContractsLoading(false);
    }
  }, []);

  const handleCardClick = () => {
    if (count <= 0) return;
    setDrawerOpen(true);
    fetchContracts();
  };

  const groupedContracts = useMemo(() => {
    const map = new Map<
      string,
      {
        title: string;
        items: any[];
        proposalId?: number;
        companyName: string;
        addressText?: string;
        cityDistrictText?: string;
      }
    >();
    contracts.forEach((appointment) => {
      const proposalIdRaw = appointment?.proposal?.id ?? appointment?.proposalId;
      const proposalId = proposalIdRaw != null ? Number(proposalIdRaw) : undefined;
      const key = proposalId != null && !Number.isNaN(proposalId) ? String(proposalId) : 'unknown';
      const companyName = appointment?.companyName ?? appointment?.companyName ?? 'Firma bilgisi yok';
      const addressText = resolveAddressText(appointment);
      const cityDistrictText = resolveCityDistrictText(appointment);
      const proposalTitleParts = [
        companyName !== 'Firma bilgisi yok' ? companyName : undefined,
      ].filter(Boolean);
      const existing = map.get(key);
      if (existing) {
        existing.items.push(appointment);
        if (!existing.addressText && addressText) {
          existing.addressText = addressText;
        }
        if (!existing.cityDistrictText && cityDistrictText) {
          existing.cityDistrictText = cityDistrictText;
        }
      } else {
        map.set(key, {
          title: proposalTitleParts.length ? proposalTitleParts.join(' · ') : `Teklif ${key}`,
          items: [appointment],
          proposalId,
          companyName,
          addressText,
          cityDistrictText,
        });
      }
    });
    return Array.from(map.values());
  }, [contracts]);

  React.useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  const cardProps = count > 0 ? { hoverable: true, onClick: handleCardClick, className: 'katip-card-clickable' } : {};

  return (
    <>
      <Card
        bordered={false}
        {...cardProps}
        className="dashboard-card dashboard-card-bg-purple"
        bodyStyle={{ padding: 10, cursor: count > 0 ? 'pointer' : undefined }}
      >
        <div className='dashboard-container'>
          <Row justify='space-between' align='middle'>
            <div className='card-value'>
              {loading ? <Spin size='small' /> : count}
            </div>
            <GoogleDocIcon size={32} color='000000e0' />
          </Row>
          <div>
            <Title style={{ fontWeight: 'bold' }} className='mb-0' level={3}>İSG-KATİP Bekleyen İşlemler</Title>
          </div>
        </div>
      </Card>
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={600}
        title="İSG-KATİP Bekleyen İşlemler"
        bodyStyle={{ padding: 16 }}
      >
        {contractsLoading ? (
          <Spin />
        ) : (
          <>
            {groupedContracts.length ? (
              groupedContracts.map((group) => {
                const startDates = group.items
                  .map((item) => (item?.startDate ? dayjs(item.startDate) : null))
                  .filter((value): value is Dayjs => value != null && value.isValid());
                const endDates = group.items
                  .map((item) => (item?.endDate ? dayjs(item.endDate) : null))
                  .filter((value): value is Dayjs => value != null && value.isValid());
                const earliestStart = startDates.reduce<dayjs.Dayjs | null>((prev, current) => {
                  if (!prev) return current;
                  if (!current) return prev;
                  return current.isBefore(prev) ? current : prev;
                }, startDates[0] ?? null);
                const latestEnd = endDates.reduce<dayjs.Dayjs | null>((prev, current) => {
                  if (!prev) return current;
                  if (!current) return prev;
                  return current.isAfter(prev) ? current : prev;
                }, endDates[0] ?? null);
                const earliestStartText = earliestStart ? earliestStart.format('DD.MM.YYYY HH:mm') : 'Tarih yok';
                const latestEndText = latestEnd ? latestEnd.format('DD.MM.YYYY HH:mm') : 'Tarih bilinmiyor';
                const targetLink = group.proposalId ? `/inspector/planning/detail/${group.proposalId}` : undefined;
                const addressText = group.addressText ?? 'Adres bilgisi yok';
                const cityDistrictText = group.cityDistrictText ?? 'İlçe/İl bilgisi yok';

                const cardContent = (
                  <Card
                    key={group.title}
                    type='inner'
                    title={group.title}
                    className='mb-16px'
                    headStyle={{ backgroundColor: '#f0f0f0', color: '#333E48' }}
                  >
                    <div>
                      <div><strong>Başlangıç:</strong> {earliestStartText}</div>
                      <div><strong>Bitiş:</strong> {latestEndText}</div>
                      <div><strong>Adres:</strong> {addressText} {cityDistrictText}</div>
                    </div>
                  </Card>
                );
                if (targetLink) {
                  return (
                    <Link to={targetLink} key={group.title}>
                      {cardContent}
                    </Link>
                  );
                }
                return cardContent;
              })
            ) : (
              <Empty description='Yeni kayıt bulunamadı' />
            )}
          </>
        )}
      </Drawer>
    </>
  );
};

export default InspectorKatipSummaryCard;
